/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/**
 * Anchor links into long pages land short of their target.
 *
 * Pages like /pages/the-community stack ~20 lazy-loaded images above a linked
 * section. When the browser resolves `#...` on load, those images have no
 * reserved height yet, so it scrolls to a document offset that is correct at
 * that instant and stale a moment later: as the images decode, everything below
 * them is pushed down and the target ends up well past the viewport. Measured on
 * the homepage's "curate pictures online" link, the landing point was 1173px
 * short - two whole sections above the intended one.
 *
 * The link targets themselves are correct; only the timing is wrong. So re-apply
 * the browser's own jump once layout has settled.
 *
 * Bails out the moment the visitor touches the page, so this can never fight
 * someone who started scrolling on their own during the settle window.
 */
(function () {
  var hash = window.location.hash;
  if (!hash || hash.length < 2) {
    return;
  }

  var targetId;
  try {
    targetId = decodeURIComponent(hash.slice(1));
  } catch (e) {
    targetId = hash.slice(1);
  }

  var visitorTookOver = false;
  var releaseControl = function () {
    visitorTookOver = true;
  };

  ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (event) {
    window.addEventListener(event, releaseControl, { passive: true, once: true });
  });

  var snapToTarget = function () {
    if (visitorTookOver) {
      return;
    }

    var target = document.getElementById(targetId);

    if (target) {
      target.scrollIntoView();
    }
  };

  window.addEventListener('load', function () {
    snapToTarget();
    // On slower connections images can still be decoding after `load` fires.
    window.setTimeout(snapToTarget, 400);
  });
})();

/**
 * Shopify Inbox's "Need help?" bubble (<shopify-chat>) renders inside an
 * open shadow root, but only exports two named parts to outside CSS:
 * ::part(activator) (the whole button) and ::part(dialog) (the panel). The
 * label span inside the button isn't an exported part, so a stylesheet
 * alone can't target just the text - not even behind a media query - which
 * is why this can't be done as a CSS-only fix.
 *
 * The component already collapses the activator into an icon-only circle
 * when its configured label is empty (its own stylesheet has a
 * `:has(.activator__label[hidden])` rule for exactly that). This reuses
 * that same mechanism instead of inventing a new one: toggle `hidden` on
 * the label span to match the theme's phone breakpoint.
 *
 * The tricky part: before the chat is opened for the first time, the
 * activator lives directly in <shopify-chat>'s own shadow root. The first
 * time it's opened, control permanently hands off to a nested
 * <shopify-agent-runtime> element with its OWN shadow root (exported back
 * out via `exportparts="activator, dialog"`), and every open/close after
 * that re-renders the activator inside THAT shadow root instead - closing
 * the panel does not bring it back to the outer one. So this can't just
 * watch one shadow root: it has to watch every shadow root it finds,
 * including new ones that show up later, wherever the activator ends up.
 *
 * Caveat: this reaches into shopify-chat's internal, unversioned markup.
 * A future update to https://cdn.shopify.com/storefront/web-components/chat.js
 * could rename `.activator__label` or restructure the nesting, silently
 * breaking this - if the label text reappears on mobile, check there first.
 */
(function () {
  var chatElement = document.querySelector('shopify-chat');
  if (!chatElement) {
    return;
  }

  var phoneQuery = window.matchMedia(window.themeVariables.breakpoints.phone);
  var watchedRoots = new WeakSet();

  function syncLabel(label) {
    if (!label.textContent.trim()) {
      // No configured label at all: Inbox already hides it on every breakpoint, leave that alone.
      return;
    }
    label.toggleAttribute('hidden', phoneQuery.matches);
  }

  // Syncs every activator label currently reachable from `root`, and starts
  // watching any shadow root found along the way (including ones nested
  // inside other shadow roots) so future re-renders keep getting caught.
  function syncAndWatch(root) {
    root.querySelectorAll('.activator__label').forEach(syncLabel);

    root.querySelectorAll('*').forEach(function (el) {
      if (!el.shadowRoot) {
        return;
      }
      // Always recurse to sync labels in nested shadow roots, even ones
      // already being watched - only the observer itself is one-time.
      syncAndWatch(el.shadowRoot);
      if (!watchedRoots.has(el.shadowRoot)) {
        watchedRoots.add(el.shadowRoot);
        new MutationObserver(function () {
          syncAndWatch(el.shadowRoot);
        }).observe(el.shadowRoot, { childList: true, subtree: true });
      }
    });
  }

  function start() {
    var root = chatElement.shadowRoot;
    if (!root || watchedRoots.has(root)) {
      return;
    }
    watchedRoots.add(root);
    syncAndWatch(root);
    new MutationObserver(function () {
      syncAndWatch(root);
    }).observe(root, { childList: true, subtree: true });
    phoneQuery.addListener(function () {
      syncAndWatch(root);
    });
  }

  try {
    if (chatElement.shadowRoot) {
      start();
    } else {
      customElements.whenDefined('shopify-chat').then(start);
    }
  } catch (e) {
    // shopify-chat's internal shadow DOM shape changed underneath us; nothing to fall back to here.
  }
})();

/*
  Collection/search filter list open/close toggle ("Close filters" / "Open filters"),
  ported from the legacy theme's theme.js (ticket 46). Delegated on document because
  theme.js replaces #facet-filters' innerHTML on every filter change, which would drop
  a listener bound directly to the link. Labels come from data attributes so the
  swapped text is translated (legacy hardcoded English).
*/
document.addEventListener('click', function (event) {
  var toggle = event.target.closest && event.target.closest('.product-facet__filter-toggle');
  if (!toggle) {
    return;
  }
  event.preventDefault();
  var list = document.querySelector('.product-facet__filter-list');
  var label = toggle.querySelector('span');
  if (!list || !label) {
    return;
  }
  var closing = !list.classList.contains('close');
  list.classList.toggle('close', closing);
  toggle.classList.toggle('toggled', closing);
  label.textContent = closing ? toggle.dataset.openLabel : toggle.dataset.closeLabel;
});

/* Header mail icon: open the Klaviyo newsletter popup (mobile or desktop form) */
document.addEventListener('click', function(event) {
  var button = event.target.closest && event.target.closest('[data-klaviyo-form-desktop]');
  if (!button) {
    return;
  }
  event.preventDefault();
  var isMobile = window.matchMedia('(max-width: 740px)').matches;
  var formId = isMobile ? button.dataset.klaviyoFormMobile : button.dataset.klaviyoFormDesktop;
  window._klOnsite = window._klOnsite || [];
  window._klOnsite.push(['openForm', formId]);
});

/* Klaviyo popup: close when clicking the overlay outside the card (Klaviyo only closes on the "x") */
document.addEventListener('click', function(event) {
  var dialog = document.querySelector('div[role="dialog"][aria-label="POPUP Form"]');
  if (!dialog || !dialog.contains(event.target)) {
    return;
  }
  var card = dialog.firstElementChild;
  var closeButton = dialog.querySelector('button[aria-label="Close dialog"]');
  if (card && closeButton && !card.contains(event.target)) {
    closeButton.click();
  }
});
